import React from 'react';
import { Box, styled } from '@mui/material';
import { AllocationRule } from 'further-types/lp/allocations';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { useFormContext } from 'react-hook-form';
import { UpdateCapitalActionFormValues } from '../../../hooks/useCapitalActionForm';
import CapitalCallAllocations from '../CapitalCallAllocations';
import useActionAllocationsChangeHandler from './hooks/useActionAllocationsChangeHandler';

type Investment = {
  id: string;
  assetName: string;
  dealName: string;
  dealCompletionDate: CalendarDay;
  holdingClasses: Array<string>;
  uncalledAmountLcy: number;
  units: number;
  averageUnitPriceLcy: number | null;
  allocationRule?: AllocationRule;
  fundingSource: FundingSource;
};

type Props = {
  vehicleId: string;
  entryIndex: number;
  baseCurrency: CurrencyCode;
  investments: Array<Investment>;
};

const AllocationsWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const InvestmentsDetails: React.FC<Props> = ({
  vehicleId,
  investments,
  baseCurrency,
  entryIndex,
}) => {
  const { watch } = useFormContext<UpdateCapitalActionFormValues>();
  const capitalBreakdown = watch(`entries.${entryIndex}.capitalBreakdown`);
  const handleAllocationsChange = useActionAllocationsChangeHandler(entryIndex);

  const { expandedRows, toggleRowExpansion } = useExpandedRows([]);

  const columns: Array<TableColumn<Investment>> = [
    {
      key: 'dealName',
      label: 'Investment',
      render: (investment) => (
        <ColorIndicatorWithText
          text={investment.dealName}
          colorKey={{ name: investment.dealName }}
        />
      ),
    },
    {
      key: 'dealCompletionDate',
      label: 'Deal completion date',
      render: (investment) => investment.dealCompletionDate.toReadable(),
    },
    {
      key: 'uncalledAmountLcy',
      label: 'To be called / (distributed)',
      render: (investment) =>
        numberToCurrencyString(investment.uncalledAmountLcy, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={investments}
      isExpandableRow={() => true}
      isExpandedRow={({ id }) => expandedRows.has(id)}
      onRowExpand={({ id }) => toggleRowExpansion(id)}
      onRowClick={({ id }) => toggleRowExpansion(id)}
      getExpandedContent={(investment) => (
        <AllocationsWrapper>
          <CapitalCallAllocations
            entryIndex={entryIndex}
            calledFundsLabel="Investment capital call"
            currency={baseCurrency}
            actionId={investment.id}
            allocationRule={AllocationRule.LinkedAction}
            capitalCalledLcy={investment.uncalledAmountLcy}
            vehicleId={vehicleId}
            capitalBreakdown={capitalBreakdown ?? []}
            keepCallAmountFixed
            onChange={(breakdown) => {
              handleAllocationsChange(investment.id, breakdown);
            }}
          />
        </AllocationsWrapper>
      )}
    />
  );
};

export default InvestmentsDetails;
